import { useState, useCallback } from 'react';
import { Holding, ClientInfo, PlanningChecklist, LifetimeIncomeInputs } from '@/types/portfolio';
import { SAMPLE_HOLDINGS, PARTNER_SAMPLE_HOLDINGS, SAMPLE_PARTNER } from '@/lib/sample-data';
import { SampleAccount } from '@/components/portfolio/LinkedAccountsPanel';

interface SampleDataState {
  holdings: Holding[];
  accounts: SampleAccount[];
  clientInfo: Partial<ClientInfo>;
  checklist: PlanningChecklist;
  lifetimeIncomeInputs: LifetimeIncomeInputs;
}

interface UseSampleDataReturn {
  sampleAccounts: SampleAccount[];
  loadSampleData: () => SampleDataState;
  clearSampleData: () => void;
}

/**
 * Hook to manage sample portfolio data for demonstration purposes.
 * Extracts complex sample data loading logic from PortfolioDashboard.
 */
export function useSampleData(
  setSamplePartner: (partner: typeof SAMPLE_PARTNER, holdings: Holding[]) => void
): UseSampleDataReturn {
  const [sampleAccounts, setSampleAccounts] = useState<SampleAccount[]>([]);

  const loadSampleData = useCallback((): SampleDataState => {
    // Create sample accounts from holdings by grouping
    const brokerageHoldings = SAMPLE_HOLDINGS.filter(h => h.id.startsWith('br-'));
    const tradIraHoldings = SAMPLE_HOLDINGS.filter(h => h.id.startsWith('ira-'));
    const rothIraHoldings = SAMPLE_HOLDINGS.filter(h => h.id.startsWith('roth-'));

    // Partner accounts
    const partner401kHoldings = PARTNER_SAMPLE_HOLDINGS.filter(h => h.id.startsWith('p-401k-'));
    const partnerRothHoldings = PARTNER_SAMPLE_HOLDINGS.filter(h => h.id.startsWith('p-roth-'));
    const partnerBrokerageHoldings = PARTNER_SAMPLE_HOLDINGS.filter(h => h.id.startsWith('p-br-'));

    const calcTotal = (holdings: Holding[]) =>
      holdings.reduce((sum, h) => sum + h.shares * h.currentPrice, 0);

    const accounts: SampleAccount[] = [
      {
        id: 'sample-brokerage',
        institution_name: 'Vanguard',
        account_name: 'Brokerage Account',
        account_type: 'Taxable',
        account_mask: '4821',
        total_value: calcTotal(brokerageHoldings),
      },
      {
        id: 'sample-trad-ira',
        institution_name: 'Fidelity',
        account_name: 'Traditional IRA',
        account_type: 'Tax-Advantaged',
        account_mask: '7392',
        total_value: calcTotal(tradIraHoldings),
      },
      {
        id: 'sample-roth-ira',
        institution_name: 'Schwab',
        account_name: 'Roth IRA',
        account_type: 'Tax-Advantaged',
        account_mask: '1056',
        total_value: calcTotal(rothIraHoldings),
      },
      // Partner accounts
      {
        id: 'sample-partner-401k',
        institution_name: 'Fidelity',
        account_name: "Sarah's 401(k)",
        account_type: 'Tax-Advantaged',
        account_mask: '8834',
        total_value: calcTotal(partner401kHoldings),
      },
      {
        id: 'sample-partner-roth',
        institution_name: 'Vanguard',
        account_name: "Sarah's Roth IRA",
        account_type: 'Tax-Advantaged',
        account_mask: '2291',
        total_value: calcTotal(partnerRothHoldings),
      },
      {
        id: 'sample-partner-brokerage',
        institution_name: 'Fidelity',
        account_name: "Sarah's Brokerage",
        account_type: 'Taxable',
        account_mask: '5567',
        total_value: calcTotal(partnerBrokerageHoldings),
      },
    ];

    setSampleAccounts(accounts);

    // Set sample partner
    setSamplePartner(SAMPLE_PARTNER, PARTNER_SAMPLE_HOLDINGS);

    const clientInfo: Partial<ClientInfo> = {
      name: 'John & Sarah Smith',
      meetingDate: new Date().toISOString().split('T')[0],
      riskTolerance: 'Moderate',
      targetAmount: 2000000,
      yearsToGoal: 20,
      currentAge: 45,
    };

    const checklist: PlanningChecklist = {
      willTrust: true,
      healthcareDirectives: true,
      poaDirectives: true,
      emergencyFund: true,
      beneficiaryReview: true,
      executorDesignation: false,
      guardianDesignation: false,
      insuranceCoverage: true,
      digitalAssetPlan: false,
      withdrawalStrategy: false,
      investmentPolicyStatement: false,
    };

    const lifetimeIncomeInputs: LifetimeIncomeInputs = {
      coreLivingExpensesMonthly: 7200,
      discretionaryExpensesMonthly: 2800,
      healthcareLongTermCareMonthly: 1400,
      guaranteedSources: [
        {
          id: crypto.randomUUID(),
          sourceName: 'John Social Security',
          sourceType: 'social-security-client',
          monthlyAmount: 3400,
          startAge: 67,
          inflationAdj: true,
          guaranteedForLife: true,
        },
        {
          id: crypto.randomUUID(),
          sourceName: 'Sarah Social Security',
          sourceType: 'social-security-spouse',
          monthlyAmount: 2000,
          startAge: 67,
          inflationAdj: true,
          guaranteedForLife: true,
        },
        {
          id: crypto.randomUUID(),
          sourceName: 'ABC Corp Pension',
          sourceType: 'pension-client',
          monthlyAmount: 2300,
          startAge: 65,
          inflationAdj: false,
          guaranteedForLife: true,
        },
        {
          id: crypto.randomUUID(),
          sourceName: 'Prudential Annuity',
          sourceType: 'guaranteed-annuity',
          monthlyAmount: 1500,
          startAge: 70,
          inflationAdj: false,
          guaranteedForLife: true,
        },
      ],
    };

    return {
      holdings: SAMPLE_HOLDINGS,
      accounts,
      clientInfo,
      checklist,
      lifetimeIncomeInputs,
    };
  }, [setSamplePartner]);

  const clearSampleData = useCallback(() => {
    setSampleAccounts([]);
  }, []);

  return {
    sampleAccounts,
    loadSampleData,
    clearSampleData,
  };
}
